import React, { useState, useEffect, useRef } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import {
  FiUser, FiMail, FiPhone, FiLock, FiCamera, FiSave,
  FiShield, FiArrowRight, FiCheck, FiX, FiEye, FiEyeOff,
  FiAlertTriangle, FiRefreshCw, FiExternalLink, FiMessageCircle,
  FiHelpCircle, FiGrid, FiCalendar, FiBriefcase, FiEdit3,
  FiCheckCircle, FiGlobe
} from "react-icons/fi";

/* ─── CONSTANTS ───────────────────────────────────────────────────────────── */
const MEDIA_BASE = "https://api.ajplservices.tech/uploads/profile/";
const SUPPORT_EMAIL = "tristaraitsolutions@gmail.com";
const SUPPORT_PHONE = "+919313118267";
const SUPPORT_WA = "919313118267";
const SUPPORT_WEBSITE = "https://tristaraitsolutions.com/";
const SUPPORT_NAME = "Tristara IT Solutions";

const buildImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const clean = path.split("/").pop();
  return `${MEDIA_BASE}${clean}`;
};

/* ─── MAIN COMPONENT ──────────────────────────────────────────────────────── */
const AdminSettings = () => {
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");

  // Profile form
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null); // { type: "success"|"error", text }

  // Password form
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const fileRef = useRef();

  /* ── Password strength ──────────────────────────────────────────────────── */
  const passwordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "", color: "#E9EDF7" };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const map = [
      { label: "", color: "#E9EDF7" },
      { label: "Weak", color: "#EF4444" },
      { label: "Fair", color: "#F59E0B" },
      { label: "Good", color: "#3B82F6" },
      { label: "Strong", color: "#10B981" },
    ];
    return { score, ...map[score] };
  };
  const pwdStrength = passwordStrength(newPassword);

  /* ── Fetch profile ──────────────────────────────────────────────────────── */
  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await AdminService.getAdminProfile();
      if (res.success) {
        setProfile(res.data);
        setFullName(res.data.full_name || "");
        setPhone(res.data.phone || "");
      }
    } catch (err) {
      console.error("Profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Image pick ─────────────────────────────────────────────────────────── */
  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  /* ── Save profile ───────────────────────────────────────────────────────── */
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const data = new FormData();
      data.append("full_name", fullName.trim());
      data.append("phone", phone.trim());
      if (imageFile) data.append("profile_image", imageFile);

      const res = await AdminService.updateAdminProfile(data);
      if (res.success) {
        setProfileMsg({ type: "success", text: "Profile updated successfully!" });
        fetchProfile();
        setImageFile(null);
        setImagePreview(null);
      } else {
        setProfileMsg({ type: "error", text: res.message || "Update failed." });
      }
    } catch (err) {
      setProfileMsg({ type: "error", text: "Network error. Please try again." });
    } finally {
      setProfileSaving(false);
    }
  };

  /* ── Change password ────────────────────────────────────────────────────── */
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: "error", text: "New passwords do not match." });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: "error", text: "Password must be at least 8 characters." });
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await AdminService.changeAdminPassword({ oldPassword, newPassword });
      if (res.success) {
        setPasswordMsg({ type: "success", text: "Password changed successfully!" });
        setOldPassword(""); setNewPassword(""); setConfirmPassword("");
      } else {
        setPasswordMsg({ type: "error", text: res.message || "Password change failed." });
      }
    } catch (err) {
      setPasswordMsg({ type: "error", text: "Network error. Please try again." });
    } finally {
      setPasswordSaving(false);
    }
  };

  /* ── Loader ─────────────────────────────────────────────────────────────── */
  if (loading) return (
    <div style={s.loaderWrap}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
      <div style={s.loaderCard}>
        <div style={s.loaderRingWrap}>
          <div style={s.spinner} />
          <div style={s.loaderCore} />
        </div>
        <div style={s.loaderBrand}>ACHAL JEWELS</div>
        <div style={s.loaderSubText}>Loading Admin Settings...</div>
      </div>
    </div>
  );

  const avatarUrl = imagePreview || buildImageUrl(profile?.profile_image);

  return (
    <div style={s.layout}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
        .tab-btn:hover{background:#EEF2FF!important;color:#422AFB!important}
        .save-btn:hover{opacity:.9;transform:translateY(-1px)}
        input:focus,select:focus,textarea:focus{border-color:#1B2559!important;box-shadow:0 0 0 3px rgba(27,37,89,0.08)!important;outline:none}
        .contact-btn:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(0,0,0,0.1)!important}
      `}</style>

      <AdminSidebar />
      <div style={s.main}>

        {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
        <div style={s.pageHeader}>
          <div>
            <div style={s.breadcrumb}>
              <FiGrid size={10}/>
              <span style={{color:"#D4AF37",fontWeight:700}}>Admin</span>
              <FiArrowRight size={9} style={{color:"#CBD5E1"}}/>
              <span>Settings</span>
            </div>
            <h1 style={s.pageTitle}>Account Settings</h1>
            <p style={s.pageSubtitle}>Manage your profile, credentials, and system preferences.</p>
          </div>
          <div style={s.headerBadge}>
            <div style={s.adminAvatar}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" style={s.adminAvatarImg} onError={e=>e.target.style.display="none"}/>
                : <span style={s.adminAvatarLetter}>{(profile?.full_name||"A").charAt(0).toUpperCase()}</span>}
            </div>
            <div>
              <div style={s.adminName}>{profile?.full_name || profile?.username || "Admin"}</div>
              <div style={s.adminRole}>{profile?.role || "ADMIN"} · {profile?.email}</div>
            </div>
          </div>
        </div>

        {/* ── LAYOUT ──────────────────────────────────────────────────────── */}
        <div style={s.contentGrid}>

          {/* LEFT SIDEBAR: Tab Nav */}
          <div style={s.navPanel}>
            <div style={s.navPanelTitle}>NAVIGATION</div>
            {[
              { id:"profile", label:"Edit Profile", icon:<FiUser size={15}/> },
              { id:"password", label:"Change Password", icon:<FiLock size={15}/> },
              { id:"support", label:"Contact Support", icon:<FiHelpCircle size={15}/> },
            ].map(tab => (
              <button
                key={tab.id}
                className="tab-btn"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...s.tabBtn,
                  background: activeTab === tab.id ? "#1B2559" : "transparent",
                  color: activeTab === tab.id ? "#FFF" : "#64748B",
                  fontWeight: activeTab === tab.id ? 800 : 600,
                }}
              >
                <span style={{
                  ...s.tabIcon,
                  background: activeTab === tab.id ? "rgba(255,255,255,0.15)" : "#F4F7FE",
                  color: activeTab === tab.id ? "#FFF" : "#1B2559",
                }}>
                  {tab.icon}
                </span>
                {tab.label}
                {activeTab === tab.id && <FiArrowRight size={13} style={{marginLeft:"auto"}}/>}
              </button>
            ))}

            {/* Profile quick-view card */}
            <div style={s.profileCard}>
              <div style={s.profileCardAvatar}>
                {avatarUrl
                  ? <img src={avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:16}} onError={e=>e.target.style.display="none"}/>
                  : <span style={{fontSize:22,fontWeight:900,color:"#FFF"}}>{(profile?.full_name||"A").charAt(0).toUpperCase()}</span>}
              </div>
              <div style={s.profileCardName}>{profile?.full_name || "Admin"}</div>
              <div style={s.profileCardEmail}>{profile?.email}</div>
              <div style={s.profileCardBadge}>
                <FiShield size={10}/> {profile?.role || "ADMIN"}
              </div>
              <div style={s.profileCardJoined}>
                <FiCalendar size={10}/>
                {profile?.created_at
                  ? new Date(profile.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})
                  : "—"}
              </div>
            </div>
          </div>

          {/* RIGHT: Tab Content */}
          <div style={s.contentPanel}>

            {/* ── TAB: PROFILE ─────────────────────────────────────────── */}
            {activeTab === "profile" && (
              <div style={{animation:"fadeUp .25s ease"}}>
                <div style={s.sectionHeader}>
                  <div style={s.sectionIconWrap}><FiUser size={18} color="#1B2559"/></div>
                  <div>
                    <div style={s.sectionTitle}>Edit Profile</div>
                    <div style={s.sectionSub}>Update your personal information and profile photo.</div>
                  </div>
                </div>

                <form onSubmit={handleSaveProfile}>
                  {/* Photo upload */}
                  <div style={s.photoSection}>
                    <div style={s.photoWrap}>
                      <div style={s.photoBig}>
                        {avatarUrl
                          ? <img src={avatarUrl} alt="" style={s.photoBigImg} onError={e=>e.target.style.display="none"}/>
                          : <span style={s.photoBigLetter}>{(profile?.full_name||"A").charAt(0).toUpperCase()}</span>}
                      </div>
                      <button
                        type="button"
                        style={s.photoEditBtn}
                        onClick={() => fileRef.current.click()}
                        title="Change photo"
                      >
                        <FiCamera size={14}/>
                      </button>
                      <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleImagePick}/>
                    </div>
                    <div style={s.photoInfo}>
                      <div style={s.photoInfoName}>{profile?.full_name || "Admin"}</div>
                      <div style={s.photoInfoRole}>{profile?.email}</div>
                      {imageFile && (
                        <div style={s.photoFilePill}>
                          <FiCheck size={10}/> {imageFile.name}
                          <button type="button" style={s.photoFileRemove} onClick={()=>{setImageFile(null);setImagePreview(null);}}>
                            <FiX size={10}/>
                          </button>
                        </div>
                      )}
                      <div style={s.photoHint}>JPG or PNG — Max 5MB</div>
                    </div>
                  </div>

                  <div style={s.formDivider}/>

                  {/* Fields */}
                  <div style={s.formGrid}>
                    <div style={s.fieldGroup}>
                      <label style={s.fieldLabel}><FiUser size={12}/> Full Name <span style={s.req}>*</span></label>
                      <input
                        style={s.fieldInput}
                        value={fullName}
                        onChange={e=>setFullName(e.target.value)}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.fieldLabel}><FiPhone size={12}/> Phone Number <span style={s.req}>*</span></label>
                      <input
                        style={s.fieldInput}
                        value={phone}
                        onChange={e=>setPhone(e.target.value)}
                        required
                        placeholder="e.g. 9876543210"
                      />
                    </div>
                  </div>

                  {/* Read-only fields */}
                  <div style={s.formGrid}>
                    <div style={s.fieldGroup}>
                      <label style={s.fieldLabel}><FiMail size={12}/> Email Address</label>
                      <div style={s.readonlyField}>
                        {profile?.email}
                        <span style={s.readonlyBadge}>Read-only</span>
                      </div>
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.fieldLabel}><FiShield size={12}/> Role</label>
                      <div style={s.readonlyField}>
                        {profile?.role}
                        <span style={s.readonlyBadge}>Read-only</span>
                      </div>
                    </div>
                  </div>

                  {/* Feedback */}
                  {profileMsg && (
                    <div style={{
                      ...s.alertBox,
                      background: profileMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                      border: `1px solid ${profileMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`,
                      color: profileMsg.type === "success" ? "#065F46" : "#991B1B",
                    }}>
                      {profileMsg.type === "success" ? <FiCheckCircle size={15}/> : <FiAlertTriangle size={15}/>}
                      {profileMsg.text}
                    </div>
                  )}

                  <div style={s.formActions}>
                    <button
                      type="button"
                      style={s.cancelBtn}
                      onClick={() => { setFullName(profile?.full_name||""); setPhone(profile?.phone||""); setImageFile(null); setImagePreview(null); setProfileMsg(null); }}
                    >
                      Discard Changes
                    </button>
                    <button type="submit" className="save-btn" style={s.saveBtn} disabled={profileSaving}>
                      {profileSaving
                        ? <><div style={s.miniSpinner}/> Saving...</>
                        : <><FiSave size={14}/> Save Profile</>}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ── TAB: PASSWORD ─────────────────────────────────────────── */}
            {activeTab === "password" && (
              <div style={{animation:"fadeUp .25s ease"}}>
                <div style={s.sectionHeader}>
                  <div style={s.sectionIconWrap}><FiLock size={18} color="#1B2559"/></div>
                  <div>
                    <div style={s.sectionTitle}>Change Password</div>
                    <div style={s.sectionSub}>Keep your account secure with a strong password.</div>
                  </div>
                </div>

                {/* Security tips */}
                <div style={s.tipsBox}>
                  <div style={s.tipsTitle}><FiShield size={13} color="#D4AF37"/> Password Requirements</div>
                  <div style={s.tipsList}>
                    {[
                      "At least 8 characters long",
                      "Include at least one uppercase letter",
                      "Include at least one number",
                      "Include a special character (e.g. @, #, !)",
                    ].map((tip, i) => (
                      <div key={i} style={s.tipItem}>
                        <div style={{
                          ...s.tipDot,
                          background: newPassword && [
                            newPassword.length>=8,
                            /[A-Z]/.test(newPassword),
                            /[0-9]/.test(newPassword),
                            /[^A-Za-z0-9]/.test(newPassword),
                          ][i] ? "#10B981" : "#CBD5E1"
                        }}/>
                        {tip}
                      </div>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleChangePassword}>
                  {/* Old password */}
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiLock size={12}/> Current Password <span style={s.req}>*</span></label>
                    <div style={s.pwdWrap}>
                      <input
                        type={showOld ? "text" : "password"}
                        style={s.fieldInputPwd}
                        value={oldPassword}
                        onChange={e=>setOldPassword(e.target.value)}
                        required
                        placeholder="Enter your current password"
                      />
                      <button type="button" style={s.eyeBtn} onClick={()=>setShowOld(v=>!v)}>
                        {showOld ? <FiEyeOff size={15}/> : <FiEye size={15}/>}
                      </button>
                    </div>
                  </div>

                  {/* New password */}
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiLock size={12}/> New Password <span style={s.req}>*</span></label>
                    <div style={s.pwdWrap}>
                      <input
                        type={showNew ? "text" : "password"}
                        style={s.fieldInputPwd}
                        value={newPassword}
                        onChange={e=>setNewPassword(e.target.value)}
                        required
                        placeholder="Create a strong new password"
                      />
                      <button type="button" style={s.eyeBtn} onClick={()=>setShowNew(v=>!v)}>
                        {showNew ? <FiEyeOff size={15}/> : <FiEye size={15}/>}
                      </button>
                    </div>
                    {/* Strength bar */}
                    {newPassword && (
                      <div style={s.strengthWrap}>
                        <div style={s.strengthBar}>
                          {[1,2,3,4].map(i => (
                            <div key={i} style={{
                              ...s.strengthSegment,
                              background: i <= pwdStrength.score ? pwdStrength.color : "#E9EDF7",
                            }}/>
                          ))}
                        </div>
                        <span style={{...s.strengthLabel, color: pwdStrength.color}}>{pwdStrength.label}</span>
                      </div>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiLock size={12}/> Confirm New Password <span style={s.req}>*</span></label>
                    <div style={s.pwdWrap}>
                      <input
                        type={showConfirm ? "text" : "password"}
                        style={{
                          ...s.fieldInputPwd,
                          borderColor: confirmPassword && confirmPassword !== newPassword ? "#EF4444" : undefined,
                        }}
                        value={confirmPassword}
                        onChange={e=>setConfirmPassword(e.target.value)}
                        required
                        placeholder="Re-enter your new password"
                      />
                      <button type="button" style={s.eyeBtn} onClick={()=>setShowConfirm(v=>!v)}>
                        {showConfirm ? <FiEyeOff size={15}/> : <FiEye size={15}/>}
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== newPassword && (
                      <div style={s.matchError}><FiX size={11}/> Passwords do not match</div>
                    )}
                    {confirmPassword && confirmPassword === newPassword && (
                      <div style={s.matchSuccess}><FiCheck size={11}/> Passwords match</div>
                    )}
                  </div>

                  {passwordMsg && (
                    <div style={{
                      ...s.alertBox,
                      background: passwordMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                      border: `1px solid ${passwordMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`,
                      color: passwordMsg.type === "success" ? "#065F46" : "#991B1B",
                    }}>
                      {passwordMsg.type === "success" ? <FiCheckCircle size={15}/> : <FiAlertTriangle size={15}/>}
                      {passwordMsg.text}
                    </div>
                  )}

                  <div style={s.formActions}>
                    <button type="button" style={s.cancelBtn} onClick={()=>{setOldPassword("");setNewPassword("");setConfirmPassword("");setPasswordMsg(null);}}>
                      Clear
                    </button>
                    <button type="submit" className="save-btn" style={s.saveBtn} disabled={passwordSaving}>
                      {passwordSaving
                        ? <><div style={s.miniSpinner}/> Updating...</>
                        : <><FiShield size={14}/> Update Password</>}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ── TAB: SUPPORT ─────────────────────────────────────────── */}
            {activeTab === "support" && (
              <div style={{animation:"fadeUp .25s ease"}}>
                <div style={s.sectionHeader}>
                  <div style={s.sectionIconWrap}><FiHelpCircle size={18} color="#1B2559"/></div>
                  <div>
                    <div style={s.sectionTitle}>Contact Support</div>
                    <div style={s.sectionSub}>Reach our development team for technical assistance.</div>
                  </div>
                </div>

                {/* Developer card */}
                <div style={s.devCard}>
                  <div style={s.devCardLeft}>
                    <div style={s.devLogo}>
                      <FiGrid size={28} color="#D4AF37"/>
                    </div>
                    <div>
                      <div style={s.devName}>{SUPPORT_NAME}</div>
                      <div style={s.devTagline}>Enterprise Software Solutions · ERP Systems</div>
                      <a href={SUPPORT_WEBSITE} target="_blank" rel="noreferrer" style={s.devWebsite}>
                        <FiGlobe size={11}/> tristaraitsolutions.com <FiExternalLink size={10}/>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Contact methods */}
                <div style={s.contactGrid}>
                  {/* Email */}
                  <div style={s.contactCard}>
                    <div style={{...s.contactIconBox, background:"#EEF2FF"}}>
                      <FiMail size={22} color="#422AFB"/>
                    </div>
                    <div style={s.contactCardTitle}>Email Support</div>
                    <div style={s.contactCardSub}>Send us a detailed query and we'll respond within 24 hours.</div>
                    <div style={s.contactCardValue}>{SUPPORT_EMAIL}</div>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}?subject=Support Request - Achal Jewels ERP&body=Hello Tristara IT Solutions Team,%0A%0AI need assistance with the following issue:%0A%0A[Describe your issue here]%0A%0ARegards,%0A${profile?.full_name || "Admin"}`}
                      style={s.contactBtn}
                      className="contact-btn"
                    >
                      <FiMail size={14}/> Send Email
                    </a>
                  </div>

                  {/* WhatsApp */}
                  <div style={s.contactCard}>
                    <div style={{...s.contactIconBox, background:"#ECFDF5"}}>
                      <FiMessageCircle size={22} color="#25D366"/>
                    </div>
                    <div style={s.contactCardTitle}>WhatsApp Support</div>
                    <div style={s.contactCardSub}>Chat with our team directly for quick resolutions.</div>
                    <div style={s.contactCardValue}>{SUPPORT_PHONE}</div>
                    <a
                      href={`https://wa.me/${SUPPORT_WA}?text=Hello Tristara IT Solutions Team, I need support with the Achal Jewels ERP system. My name is ${profile?.full_name || "Admin"}.`}
                      target="_blank"
                      rel="noreferrer"
                      style={{...s.contactBtn, background:"#25D366"}}
                      className="contact-btn"
                    >
                      <FiMessageCircle size={14}/> WhatsApp Chat
                    </a>
                  </div>

                  {/* Phone */}
                  <div style={s.contactCard}>
                    <div style={{...s.contactIconBox, background:"#FEF3C7"}}>
                      <FiPhone size={22} color="#D97706"/>
                    </div>
                    <div style={s.contactCardTitle}>Phone Support</div>
                    <div style={s.contactCardSub}>Call us directly for urgent technical issues.</div>
                    <div style={s.contactCardValue}>{SUPPORT_PHONE}</div>
                    <a
                      href={`tel:${SUPPORT_WA}`}
                      style={{...s.contactBtn, background:"#1B2559"}}
                      className="contact-btn"
                    >
                      <FiPhone size={14}/> Call Now
                    </a>
                  </div>
                </div>

                {/* System info */}
                <div style={s.sysInfoBox}>
                  <div style={s.sysInfoTitle}>System Information</div>
                  <div style={s.sysInfoGrid}>
                    <SysRow label="System" value="Achal Jewels ERP v1.0"/>
                    <SysRow label="Developer" value={SUPPORT_NAME}/>
                    <SysRow label="Admin ID" value={`#${profile?.id}`}/>
                    <SysRow label="Role" value={profile?.role}/>
                    <SysRow label="Account Email" value={profile?.email}/>
                    <SysRow label="Registered" value={profile?.created_at ? new Date(profile.created_at).toLocaleDateString("en-IN") : "—"}/>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── HELPER COMPONENTS ───────────────────────────────────────────────────── */
const SysRow = ({ label, value }) => (
  <div style={s.sysRow}>
    <span style={s.sysRowLabel}>{label}</span>
    <span style={s.sysRowValue}>{value || "—"}</span>
  </div>
);

/* ─── STYLES ──────────────────────────────────────────────────────────────── */
const s = {
  layout: { display:"flex", background:"#F4F7FE", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif" },
  main: { marginLeft:280, padding:"32px 36px", flex:1, minWidth:0 },

  /* Loader */
  loaderWrap: { height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#F4F7FE" },
  loaderCard: { display:"flex", flexDirection:"column", alignItems:"center", gap:12 },
  loaderRingWrap: { position:"relative", width:64, height:64, marginBottom:8 },
  spinner: { width:64, height:64, border:"3px solid #E9EDF7", borderTopColor:"#D4AF37", borderRadius:"50%", animation:"spin 1s linear infinite", position:"absolute" },
  loaderCore: { width:16, height:16, background:"#1B2559", borderRadius:"50%", position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)" },
  loaderBrand: { fontSize:15, fontWeight:900, color:"#1B2559", letterSpacing:6 },
  loaderSubText: { fontSize:12, color:"#A3AED0", fontWeight:600 },

  /* Header */
  pageHeader: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28, gap:20 },
  breadcrumb: { display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#A3AED0", fontWeight:600, marginBottom:6 },
  pageTitle: { margin:0, fontSize:26, fontWeight:900, color:"#1B2559", letterSpacing:"-0.5px" },
  pageSubtitle: { margin:"4px 0 0", fontSize:13, color:"#A3AED0", fontWeight:500 },
  headerBadge: { display:"flex", alignItems:"center", gap:14, background:"#FFF", padding:"14px 20px", borderRadius:18, border:"1px solid #E9EDF7", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" },
  adminAvatar: { width:48, height:48, borderRadius:14, background:"linear-gradient(135deg,#D4AF37,#F5D76E)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", flexShrink:0 },
  adminAvatarImg: { width:"100%", height:"100%", objectFit:"cover" },
  adminAvatarLetter: { fontSize:20, fontWeight:900, color:"#FFF" },
  adminName: { fontSize:14, fontWeight:800, color:"#1B2559" },
  adminRole: { fontSize:11, color:"#A3AED0", fontWeight:600, marginTop:2 },

  /* Layout grid */
  contentGrid: { display:"grid", gridTemplateColumns:"260px 1fr", gap:20, alignItems:"flex-start" },

  /* Nav panel */
  navPanel: { background:"#FFF", borderRadius:20, padding:"20px 14px", border:"1px solid #F0F2FA", boxShadow:"0 2px 12px rgba(0,0,0,0.04)", position:"sticky", top:20 },
  navPanelTitle: { fontSize:9, fontWeight:900, color:"#A3AED0", letterSpacing:2, textTransform:"uppercase", marginBottom:12, padding:"0 8px" },
  tabBtn: { display:"flex", alignItems:"center", gap:10, width:"100%", padding:"11px 12px", borderRadius:13, border:"none", cursor:"pointer", fontSize:13, fontFamily:"inherit", textAlign:"left", marginBottom:4, transition:"0.15s" },
  tabIcon: { width:30, height:30, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"0.15s" },

  /* Profile quick card */
  profileCard: { marginTop:20, paddingTop:16, borderTop:"1px solid #F0F2FA", display:"flex", flexDirection:"column", alignItems:"center", gap:5, textAlign:"center" },
  profileCardAvatar: { width:60, height:60, borderRadius:17, background:"linear-gradient(135deg,#D4AF37,#F5D76E)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", marginBottom:4 },
  profileCardName: { fontSize:14, fontWeight:800, color:"#1B2559" },
  profileCardEmail: { fontSize:11, color:"#A3AED0", fontWeight:500 },
  profileCardBadge: { display:"inline-flex", alignItems:"center", gap:5, background:"#EEF2FF", color:"#422AFB", padding:"4px 10px", borderRadius:20, fontSize:10, fontWeight:800, marginTop:4 },
  profileCardJoined: { display:"flex", alignItems:"center", gap:5, fontSize:10, color:"#CBD5E1", fontWeight:600, marginTop:4 },

  /* Content panel */
  contentPanel: { background:"#FFF", borderRadius:20, padding:"28px 32px", border:"1px solid #F0F2FA", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" },

  /* Section header */
  sectionHeader: { display:"flex", alignItems:"center", gap:14, marginBottom:28, paddingBottom:20, borderBottom:"1px solid #F4F7FE" },
  sectionIconWrap: { width:46, height:46, borderRadius:14, background:"#F4F7FE", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  sectionTitle: { fontSize:17, fontWeight:900, color:"#1B2559", letterSpacing:"-0.3px" },
  sectionSub: { fontSize:12, color:"#A3AED0", fontWeight:500, marginTop:3 },

  /* Photo section */
  photoSection: { display:"flex", alignItems:"center", gap:24, marginBottom:24 },
  photoWrap: { position:"relative", flexShrink:0 },
  photoBig: { width:88, height:88, borderRadius:22, background:"linear-gradient(135deg,#D4AF37,#F5D76E)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", border:"3px solid #F0F2FA" },
  photoBigImg: { width:"100%", height:"100%", objectFit:"cover" },
  photoBigLetter: { fontSize:32, fontWeight:900, color:"#FFF" },
  photoEditBtn: { position:"absolute", bottom:-4, right:-4, width:30, height:30, borderRadius:"50%", background:"#1B2559", border:"3px solid #FFF", color:"#FFF", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"0.2s" },
  photoInfo: { display:"flex", flexDirection:"column", gap:4 },
  photoInfoName: { fontSize:16, fontWeight:800, color:"#1B2559" },
  photoInfoRole: { fontSize:12, color:"#A3AED0", fontWeight:500 },
  photoFilePill: { display:"inline-flex", alignItems:"center", gap:6, background:"#ECFDF5", color:"#065F46", padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:700, marginTop:4 },
  photoFileRemove: { background:"none", border:"none", cursor:"pointer", color:"#065F46", display:"flex", padding:0 },
  photoHint: { fontSize:10, color:"#CBD5E1", fontWeight:500 },

  /* Form */
  formDivider: { height:1, background:"#F4F7FE", margin:"4px 0 20px" },
  formGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 },
  fieldGroup: { display:"flex", flexDirection:"column", gap:7, marginBottom:16 },
  fieldLabel: { fontSize:12, color:"#475569", fontWeight:700, display:"flex", alignItems:"center", gap:5 },
  req: { color:"#EF4444", fontSize:11 },
  fieldInput: { padding:"12px 14px", borderRadius:11, border:"1.5px solid #E9EDF7", outline:"none", fontSize:13, color:"#1B2559", fontFamily:"inherit", fontWeight:500, transition:"0.2s", background:"#FFF" },
  readonlyField: { padding:"12px 14px", borderRadius:11, border:"1.5px solid #F4F7FE", background:"#FAFBFF", fontSize:13, color:"#94A3B8", fontWeight:600, display:"flex", justifyContent:"space-between", alignItems:"center" },
  readonlyBadge: { fontSize:9, fontWeight:800, color:"#CBD5E1", textTransform:"uppercase", letterSpacing:1, background:"#F4F7FE", padding:"3px 8px", borderRadius:6 },

  /* Password */
  pwdWrap: { position:"relative" },
  fieldInputPwd: { width:"100%", padding:"12px 44px 12px 14px", borderRadius:11, border:"1.5px solid #E9EDF7", outline:"none", fontSize:13, color:"#1B2559", fontFamily:"inherit", fontWeight:500, transition:"0.2s", background:"#FFF", boxSizing:"border-box" },
  eyeBtn: { position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#94A3B8", cursor:"pointer", display:"flex", padding:2 },
  strengthWrap: { display:"flex", alignItems:"center", gap:10, marginTop:8 },
  strengthBar: { flex:1, display:"flex", gap:4 },
  strengthSegment: { flex:1, height:4, borderRadius:2, transition:"background 0.3s" },
  strengthLabel: { fontSize:11, fontWeight:800, minWidth:40 },
  matchError: { display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#EF4444", fontWeight:700, marginTop:5 },
  matchSuccess: { display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#10B981", fontWeight:700, marginTop:5 },

  /* Tips box */
  tipsBox: { background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:14, padding:"16px 18px", marginBottom:24 },
  tipsTitle: { display:"flex", alignItems:"center", gap:7, fontSize:12, fontWeight:800, color:"#92400E", marginBottom:12 },
  tipsList: { display:"flex", flexDirection:"column", gap:8 },
  tipItem: { display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#78350F", fontWeight:600 },
  tipDot: { width:7, height:7, borderRadius:"50%", flexShrink:0, transition:"background 0.3s" },

  /* Alerts */
  alertBox: { display:"flex", alignItems:"center", gap:10, padding:"13px 16px", borderRadius:12, fontSize:13, fontWeight:600, marginBottom:18 },

  /* Form actions */
  formActions: { display:"flex", justifyContent:"flex-end", gap:12, marginTop:4, paddingTop:20, borderTop:"1px solid #F4F7FE" },
  cancelBtn: { padding:"11px 22px", borderRadius:11, border:"1px solid #E9EDF7", background:"#F4F7FE", color:"#64748B", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" },
  saveBtn: { display:"flex", alignItems:"center", gap:7, padding:"11px 24px", borderRadius:11, border:"none", background:"#1B2559", color:"#FFF", fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"inherit", transition:"0.2s", boxShadow:"0 8px 20px rgba(27,37,89,0.18)" },
  miniSpinner: { width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"#FFF", borderRadius:"50%", animation:"spin .7s linear infinite" },

  /* Support tab */
  devCard: { background:"linear-gradient(135deg,#0F172A,#1B2559)", borderRadius:18, padding:"24px 26px", marginBottom:24, display:"flex", alignItems:"center", justifyContent:"space-between" },
  devCardLeft: { display:"flex", alignItems:"center", gap:18 },
  devLogo: { width:56, height:56, borderRadius:16, background:"rgba(212,175,55,0.15)", border:"1px solid rgba(212,175,55,0.3)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  devName: { fontSize:18, fontWeight:900, color:"#FFF", letterSpacing:"-0.3px" },
  devTagline: { fontSize:12, color:"rgba(255,255,255,0.5)", fontWeight:500, marginTop:3 },
  devWebsite: { display:"inline-flex", alignItems:"center", gap:5, fontSize:11, color:"#D4AF37", fontWeight:700, textDecoration:"none", marginTop:8 },

  contactGrid: { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:24 },
  contactCard: { background:"#FAFBFF", borderRadius:16, padding:"20px", border:"1px solid #F0F2FA", display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:8 },
  contactIconBox: { width:52, height:52, borderRadius:15, display:"flex", alignItems:"center", justifyContent:"center" },
  contactCardTitle: { fontSize:14, fontWeight:800, color:"#1B2559" },
  contactCardSub: { fontSize:11, color:"#94A3B8", fontWeight:500, lineHeight:1.5 },
  contactCardValue: { fontSize:12, fontWeight:700, color:"#475569", background:"#F4F7FE", padding:"5px 12px", borderRadius:8, width:"100%", wordBreak:"break-all" },
  contactBtn: { display:"flex", alignItems:"center", justifyContent:"center", gap:7, background:"#422AFB", color:"#FFF", padding:"10px 16px", borderRadius:10, fontSize:12, fontWeight:800, textDecoration:"none", width:"100%", transition:"0.2s", boxShadow:"0 4px 12px rgba(0,0,0,0.1)", boxSizing:"border-box" },

  /* System info */
  sysInfoBox: { background:"#F8FAFC", borderRadius:16, padding:"20px", border:"1px solid #F0F2FA" },
  sysInfoTitle: { fontSize:11, fontWeight:800, color:"#A3AED0", textTransform:"uppercase", letterSpacing:1, marginBottom:14 },
  sysInfoGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 24px" },
  sysRow: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #F0F2FA" },
  sysRowLabel: { fontSize:11, color:"#94A3B8", fontWeight:700 },
  sysRowValue: { fontSize:12, color:"#1B2559", fontWeight:700 },
};

export default AdminSettings;